import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export const ORGANIZATION_INVOICES_BUCKET = 'organization-invoices'

export type InvoiceRow = Database['public']['Tables']['invoice']['Row']

export function sanitizeInvoiceFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 100)
}

export function getInvoicePublicUrl(
  supabase: SupabaseClient<Database>,
  storagePath: string
): string {
  const { data } = supabase.storage
    .from(ORGANIZATION_INVOICES_BUCKET)
    .getPublicUrl(storagePath)
  return data.publicUrl
}

export const useInvoicesApi = ({ org_id }: { org_id?: string }) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  const { data: invoices = [], ...listQuery } = useQuery({
    queryKey: [queryKeys.invoices, org_id],
    queryFn: async (): Promise<InvoiceRow[]> => {
      if (!org_id) throw new Error('Missing organization')
      const { data, error } = await supabase
        .from('invoice')
        .select('*')
        .eq('organization_id', org_id)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (data ?? []) as InvoiceRow[]
    },
    enabled: !!org_id,
  })

  const uploadInvoice = useMutation({
    mutationFn: async (file: File) => {
      if (!org_id) throw new Error('Missing organization')

      const { data: userData } = await supabase.auth.getUser()
      const user = userData.user

      const base = Date.now()
      const safe = sanitizeInvoiceFilename(file.name)
      const storagePath = `${org_id}/${base}-${safe}`

      const { error: uploadError } = await supabase.storage
        .from(ORGANIZATION_INVOICES_BUCKET)
        .upload(storagePath, file)

      if (uploadError) throw uploadError

      const inserted = await supabase
        .from('invoice')
        .insert({
          organization_id: org_id,
          storage_path: storagePath,
          file_name: file.name,
          created_by: user?.id ?? null,
        })
        .select('*')
        .single()

      if (inserted.error) {
        const { error: removeError } = await supabase.storage
          .from(ORGANIZATION_INVOICES_BUCKET)
          .remove([storagePath])
        if (removeError) console.error(removeError)

        throw inserted.error
      }

      return inserted.data as InvoiceRow
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.invoices, org_id],
      })
    },
  })

  const deleteInvoice = useMutation({
    mutationFn: async (row: InvoiceRow) => {
      const { error: storageError } = await supabase.storage
        .from(ORGANIZATION_INVOICES_BUCKET)
        .remove([row.storage_path])

      if (storageError) throw storageError

      const { error } = await supabase.from('invoice').delete().eq('id', row.id)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.invoices, org_id],
      })
    },
  })

  return {
    invoices,
    isLoadingInvoices: listQuery.isPending,
    invoicesError: listQuery.error,
    uploadInvoice,
    deleteInvoice,
  }
}
